import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FaCloudUploadAlt } from 'react-icons/fa';
import DynamicSelect from './DynamicSelect';

const MEDICINE_API = 'https://api.muktihospital.com/api/medicine';
const API_KEY = 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';

const EditMedicineModal = ({ isOpen, onClose, onSave, medicine, categories, groups, companies, units }) => {
  const [medicineData, setMedicineData] = useState({
    medicineName: '',
    medicineCompany: '',
    medicineCategory: '',
    medicineGroup: '',
    unit: '',
    rackNumber: '',
    note: '',
    medicinePhoto: null, // For new file uploads (File object)
    existingPhoto: '',   // To store the existing file path or URL
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Debug: Log the medicine prop to verify its contents
  useEffect(() => {
    if (medicine) {
      // console.log('EditMedicineModal received medicine:', medicine);
      setMedicineData({
        medicineName: medicine.medicineName || '',
        medicineCompany: medicine.medicineCompany || '',
        medicineCategory: medicine.medicineCategory || '',
        medicineGroup: medicine.medicineGroup || '',
        unit: medicine.unit || '',
        rackNumber: medicine.rackNumber || '',
        note: medicine.note || '',
        medicinePhoto: null, // New file is null until user uploads
        existingPhoto: medicine.medicinePhoto || '', // Store existing photo path or URL
      });
    } else {
      console.warn('EditMedicineModal received no medicine prop');
    }
  }, [medicine]);

  // Normalize options to handle casing and whitespace
  const normalizeValue = (value) => (value ? value.toString().trim().toLowerCase() : '');

  const categoryOptions = Array.isArray(categories)
    ? categories.map(category => {
        const value = normalizeValue(category.name || category.title || Object.values(category)[0]);
        return {
          label: value || 'Unknown',
          value: value || 'Unknown',
          originalValue: category.name || category.title || Object.values(category)[0],
        };
      })
    : [];

  const groupOptions = Array.isArray(groups)
    ? groups.map(group => {
        const value = normalizeValue(group.groupName || group.companyName);
        return {
          label: value || 'Unknown',
          value: value || 'Unknown',
          originalValue: group.groupName || group.companyName,
        };
      })
    : [];

  const companyOptions = Array.isArray(companies)
    ? companies.map(company => {
        const value = normalizeValue(company.companyName);
        return {
          label: value || 'Unknown',
          value: value || 'Unknown',
          originalValue: company.companyName,
        };
      })
    : [];

  const unitOptions = Array.isArray(units)
    ? units.map(unit => {
        const value = normalizeValue(unit.name || unit.title || Object.values(unit)[0]);
        return {
          label: value || 'Unknown',
          value: value || 'Unknown',
          originalValue: unit.name || unit.title || Object.values(unit)[0],
        };
      })
    : [];

  // Find option for DynamicSelect, fallback to custom option if no match
  const findOption = (value, options) => {
    if (!value) return null;
    const normalizedValue = normalizeValue(value);
    const matchedOption = options.find(option => normalizeValue(option.value) === normalizedValue);
    if (matchedOption) return matchedOption;
    return { label: value, value: value, originalValue: value };
  };

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    if (!medicineData.medicineName.trim()) newErrors.medicineName = 'Medicine Name is required';
    if (!medicineData.medicineCategory.trim()) newErrors.medicineCategory = 'Medicine Category is required';
    if (!medicineData.medicineCompany.trim()) newErrors.medicineCompany = 'Medicine Company is required';
    if (!medicineData.medicineGroup.trim()) newErrors.medicineGroup = 'Medicine Group is required';
    if (!medicineData.unit.trim()) newErrors.unit = 'Unit is required';
    setErrors(newErrors);
    // console.log('Validation errors:', newErrors); // Debug validation errors
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setMedicineData(prev => ({
      ...prev,
      [field]: value,
    }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSelectChange = (field, selectedOption) => {
    setMedicineData(prev => ({
      ...prev,
      [field]: selectedOption ? selectedOption.originalValue || selectedOption.value : '',
    }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMedicineData(prev => ({
        ...prev,
        medicinePhoto: file, // Store new file
        existingPhoto: '',   // Clear existing photo when new file is selected
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }
    setIsLoading(true);
    try {
      const payload = new FormData();
      const fields = {
        medicineName: medicineData.medicineName,
        medicineCategory: medicineData.medicineCategory,
        medicineCompany: medicineData.medicineCompany,
        medicineGroup: medicineData.medicineGroup,
        unit: medicineData.unit,
        rackNumber: medicineData.rackNumber,
        note: medicineData.note,
      };

      Object.entries(fields).forEach(([key, value]) => {
        if (value !== '' && value !== null) payload.append(key, value);
      });

      // Only append medicinePhoto if a new file is selected
      if (medicineData.medicinePhoto) {
        payload.append('medicinePhoto', medicineData.medicinePhoto);
      }

      // console.log('Sending payload to API:', Object.fromEntries(payload)); // Debug payload

      const response = await axios.put(`${MEDICINE_API}/edit/${medicine.id}`, payload, {
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'multipart/form-data',
        },
      });

      // console.log('API response:', response.data);
      onSave(medicine.id, response.data.medicine);
      onClose();
    } catch (error) {
      console.error('Error updating medicine:', error.response?.data || error.message); // Debug error
      toast.error(error.response?.data?.error || 'Failed to update medicine. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-full sm:max-w-[80%] md:max-w-[700px] lg:max-w-screen-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">Edit Medicine Details</h2>
        </div>
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Medicine Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medicine Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={medicineData.medicineName}
              onChange={(e) => handleFieldChange('medicineName', e.target.value)}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 text-sm ${errors.medicineName ? 'border-red-500' : ''}`}
              placeholder="Medicine Name"
              disabled={isLoading}
            />
            {errors.medicineName && (
              <span className="text-red-500 text-xs mt-1">{errors.medicineName}</span>
            )}
          </div>
          {/* Medicine Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medicine Category <span className="text-red-500">*</span>
            </label>
            <DynamicSelect
              options={categoryOptions}
              onChange={(selected) => handleSelectChange('medicineCategory', selected)}
              value={findOption(medicineData.medicineCategory, categoryOptions)}
              isSearchable={true}
              placeholder="Select"
              isDisabled={isLoading}
            />
            {errors.medicineCategory && (
              <span className="text-red-500 text-xs mt-1">{errors.medicineCategory}</span>
            )}
          </div>
          {/* Medicine Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medicine Company <span className="text-red-500">*</span>
            </label>
            <DynamicSelect
              options={companyOptions}
              onChange={(selected) => handleSelectChange('medicineCompany', selected)}
              value={findOption(medicineData.medicineCompany, companyOptions)}
              isSearchable={true}
              placeholder="Select"
              isDisabled={isLoading}
            />
            {errors.medicineCompany && (
              <span className="text-red-500 text-xs mt-1">{errors.medicineCompany}</span>
            )}
          </div>
          {/* Medicine Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Medicine Group <span className="text-red-500">*</span>
            </label>
            <DynamicSelect
              options={groupOptions}
              onChange={(selected) => handleSelectChange('medicineGroup', selected)}
              value={findOption(medicineData.medicineGroup, groupOptions)}
              isSearchable={true}
              placeholder="Select"
              isDisabled={isLoading}
            />
            {errors.medicineGroup && (
              <span className="text-red-500 text-xs mt-1">{errors.medicineGroup}</span>
            )}
          </div>
          {/* Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit <span className="text-red-500">*</span>
            </label>
            <DynamicSelect
              options={unitOptions}
              onChange={(selected) => handleSelectChange('unit', selected)}
              value={findOption(medicineData.unit, unitOptions)}
              isSearchable={true}
              placeholder="Select"
              isDisabled={isLoading}
            />
            {errors.unit && (
              <span className="text-red-500 text-xs mt-1">{errors.unit}</span>
            )}
          </div>
          {/* Rack Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rack Number</label>
            <input
              type="text"
              value={medicineData.rackNumber}
              onChange={(e) => handleFieldChange('rackNumber', e.target.value)}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm"
              placeholder="Rack Number"
              disabled={isLoading}
            />
          </div>
           {/* Medicine Photo */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Medicine Photo (JPG, JPEG, PNG)</label>
            <div className="relative">
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm opacity-0 absolute z-10"
                disabled={isLoading}
              />
              <div className="w-full p-2 border rounded-md text-sm text-gray-500 flex items-center justify-between">
                <span>
                  {medicineData.medicinePhoto 
                    ? medicineData.medicinePhoto.name 
                    : medicineData.existingPhoto || 'Drop a file here or click'}
                </span>
                <FaCloudUploadAlt className="text-gray-400" size={20} />
              </div>
            </div>
          </div>
          {/* Note */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea
              value={medicineData.note}
              onChange={(e) => handleFieldChange('note', e.target.value)}
              className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm"
              rows="3"
              placeholder="Note"
              disabled={isLoading}
            />
          </div>      
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md transition-all duration-200 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-M-primary-color'}`}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMedicineModal;